import React, { useState } from 'react';
import { AlertTriangle, Trash2, Loader2, ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../App';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';
import { Textarea } from '../ui/textarea';

const BLOCKER_LABELS = {
  open_bookings: (n) => `You have ${n} active booking${n === 1 ? '' : 's'} (pending, confirmed, or awaiting reschedule). Please complete or cancel these first.`,
  disputed_bookings: (n) => `${n} booking${n === 1 ? ' is' : 's are'} under dispute. Wait for resolution from the support team.`,
  payouts_in_flight: (n) => `${n} payout${n === 1 ? ' is' : 's are'} still being processed by your bank. Please wait until Monday\'s sweep completes.`,
  unpaid_balance: (v) => `You have £${Number(v).toFixed(2)} in unpaid earnings. Wait until the next Monday payout clears, then try again.`,
};

export function DeleteAccountCard({ role }) {
  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const openDialog = async () => {
    setLoading(true);
    try {
      const res = await api.post('/auth/me/deletion-preflight');
      setPreflight(res.data);
      setConfirmOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not check account status');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await api.post('/auth/me/request-deletion', { reason });
      toast.success('Account deactivated. You have 30 days to reactivate by contacting support.');
      // Log out locally so the session closes cleanly.
      try { await api.post('/auth/logout'); } catch { /* noop */ }
      window.location.href = '/';
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.blockers) {
        setPreflight({ can_delete: false, blockers: detail.blockers, grace_days: 30 });
        toast.error(detail.message || 'Account has outstanding items');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to delete account');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const blockers = preflight?.blockers || {};
  const hasBlockers = Object.keys(blockers).length > 0;
  const graceDays = preflight?.grace_days || 30;

  return (
    <div className="card-surface p-4 sm:p-6" data-testid="delete-account-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
          <Trash2 className="h-5 w-5 text-red-500" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Delete account</h3>
          <p className="text-sm text-stone-500 mt-1 leading-relaxed">
            Permanently remove your Zuriya account. We'll deactivate it immediately,
            then anonymise your data after {graceDays} days. Bookings are kept for accounting (HMRC 6-year requirement) but your name, email and contact details are scrubbed.
          </p>
          <Button
            variant="outline"
            onClick={openDialog}
            disabled={loading}
            className="mt-3 border-red-200 text-red-600 hover:bg-red-50 gap-2"
            data-testid="delete-account-open-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Delete account
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="max-w-md" data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {hasBlockers ? 'Cannot delete yet' : 'Delete account?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-stone-600 leading-relaxed">
              {hasBlockers ? (
                <>
                  Before we can deactivate your account, please sort the following:
                </>
              ) : (
                <>
                  Your account will be deactivated immediately. You have <strong>{graceDays} days</strong> to
                  reactivate by contacting support. After that, your data will be permanently anonymised.
                  {role === 'provider' && ' Your storefront will go offline and no new bookings will be accepted.'}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {hasBlockers ? (
            <div className="space-y-2" data-testid="delete-account-blockers">
              {Object.entries(blockers).map(([key, value]) => (
                <div key={key} className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-100 px-3 py-2">
                  <ShieldAlert className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">
                    {BLOCKER_LABELS[key] ? BLOCKER_LABELS[key](value) : `${key}: ${value}`}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-xs font-medium text-stone-600">
                Optional, help us improve (won't affect the deletion):
              </label>
              <Textarea
                placeholder="Why are you leaving?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                className="border-[#E6E4DD]"
                data-testid="delete-account-reason"
              />
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-account-cancel">
              <X className="h-4 w-4 mr-1" /> Keep my account
            </AlertDialogCancel>
            {!hasBlockers && (
              <AlertDialogAction
                onClick={handleConfirm}
                disabled={submitting}
                className="bg-red-500 hover:bg-red-600 gap-2"
                data-testid="delete-account-confirm"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {submitting ? 'Deactivating...' : 'Delete account'}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
